'use client'

import { type ReadonlyURLSearchParams, useSearchParams } from 'next/navigation'
import React, { type ComponentProps, Suspense, useRef } from 'react'
import AppLinkSSRFallback from '@/components/elementary/AppLink/SSRAppLink'
import type { AppLinkProps } from '@/components/elementary/AppLink/types'
import {
	determineLinkVariant,
	getLinkExceptions,
	renderContent,
} from '@/components/elementary/AppLink/utils'
import { buttonVariants } from '@/components/ui/button'
import { Link } from '@/i18n/navigation'
import { formatEventName, useAnalytics } from '@/lib/analytics/analytics'
import { ANALYTICS_EVENTS } from '@/lib/analytics/analytics.constants'
import { extractTextFromChildren, getOriginUrl } from '@/lib/general-helpers'
import { cn } from '@/lib/styles'
import { isAppLink } from '@/utils/links'

// query params that should be persisted across navigation
const allowedParams = [
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_content',
	'utm_term',
	'utm_id',
	'utm_campaign_id',
	'utm_partner_source',
	'gclid',
	'li_fat_id',
	'_ga',
]

const createQueryString = (params: ReadonlyURLSearchParams) => {
	const filteredParams = new URLSearchParams()

	for (const key of allowedParams) {
		const value = params.get(key)
		if (value !== null) {
			filteredParams.set(key, value)
		}
	}
	const result = filteredParams.toString()
	return result ? `?${result}` : ''
}

const AppLinkComponent = React.forwardRef<HTMLAnchorElement, AppLinkProps>(
	(
		{
			href,
			label,
			className,
			children,
			iconRight,
			iconLeft,
			openExternalInNewTab = false,
			size = 'none',
			underline = false,
			trackingContext,
			onClick,
			...props
		},
		ref,
	) => {
		const searchParams = useSearchParams()
		const { track } = useAnalytics()
		const pointerDownTrackedRef = useRef(false)
		const variant = determineLinkVariant(href, label || children?.toString(), props.variant)

		const combinedClassName = cn(buttonVariants({ variant, size }), className)
		const linkClassName = cn(
			'group/app-link flex relative flex-row items-center gap-1 h-fit',
			underline && 'group-hover/image-link:border-b-brand-black hover:border-b-brand-black',
			variant !== 'google' && 'border-b border-transparent',
			combinedClassName,
		)

		const linkException = getLinkExceptions(
			{
				href,
				ref,
				linkClassName,
				props,
			},
			() =>
				renderContent({
					variant,
					iconLeft,
					iconRight,
					label,
					children,
				}),
		)
		if (linkException) {
			return linkException
		}

		const isExternal = typeof href === 'string' && !isAppLink(href)

		// Track on pointerdown for external links — eg. knock.ai widgets (start-chat)
		// swallow click events via stopPropagation, so onClick never fires.
		const handlePointerDown = (event: React.PointerEvent<HTMLAnchorElement>) => {
			if (event.button !== 0) return

			const linkText = label || extractTextFromChildren(children) || ''
			track({
				eventType: formatEventName(ANALYTICS_EVENTS.LINK_CLICKED),
				eventProperties: {
					linkUrl: String(href),
					linkText,
					...trackingContext,
				},
			})
			pointerDownTrackedRef.current = true
		}

		const handleExternalClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
			// Track only if pointerdown didn't already (keyboard navigation case)
			if (!pointerDownTrackedRef.current) {
				const linkText = label || extractTextFromChildren(children) || ''
				track({
					eventType: formatEventName(ANALYTICS_EVENTS.LINK_CLICKED),
					eventProperties: {
						linkUrl: String(href),
						linkText,
						...trackingContext,
					},
				})
			}
			pointerDownTrackedRef.current = false
			onClick?.(event)
		}

		const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
			const linkText = label || extractTextFromChildren(children) || ''
			track({
				eventType: formatEventName(ANALYTICS_EVENTS.LINK_CLICKED),
				eventProperties: {
					linkUrl: String(href),
					linkText,
					...trackingContext,
				},
			})
			onClick?.(event)
		}

		if (isExternal) {
			return (
				<a
					href={`${new URL(href)}${createQueryString(searchParams)}`}
					ref={ref}
					target={openExternalInNewTab ? '_blank' : ''}
					rel={openExternalInNewTab ? 'noopener noreferrer' : ''}
					className={linkClassName}
					onPointerDown={handlePointerDown}
					onClick={handleExternalClick}
					{...props}
				>
					{renderContent({
						variant,
						iconLeft,
						iconRight,
						label,
						children,
					})}
				</a>
			)
		}

		const hrefWithQueryString =
			typeof href === 'string'
				? `${new URL(href, getOriginUrl()).pathname}${createQueryString(searchParams)}`
				: href

		return (
			<Link
				href={
					/* As-assertion needed due to widened AppLinkProps.href type. */
					(typeof href === 'string' && href?.startsWith('#')
						? href
						: hrefWithQueryString) as ComponentProps<typeof Link>['href']
				}
				ref={ref}
				className={linkClassName}
				onClick={handleClick}
				{...props}
			>
				{renderContent({
					variant,
					iconLeft,
					iconRight,
					label,
					children,
				})}
			</Link>
		)
	},
)

const AppLinkDefaultComponent = React.forwardRef<HTMLAnchorElement, AppLinkProps>((props, ref) => (
	<Suspense fallback={<AppLinkSSRFallback {...props} ref={ref} />}>
		<AppLinkComponent {...props} ref={ref} />
	</Suspense>
))

export default AppLinkDefaultComponent
